import React from 'react';
import { log } from '@economic/react-app';
import internalApiClient from '../util/api/internalApiClient';
import restApiClient from '../util/api/restApiClient';
import { useQueryState } from './useQueryState';
import getAddonsApiClient from '../util/api/addonsApiClient';
import getMarketApiClient from '../util/api/marketApiClient';
import getWorkflowsApiClient from '../util/api/workflowsApiClient';
import getHqApiClient from '../util/api/hqApiClient';
import { QueryOptionsApi } from './QueryOptionsApi';

export interface QueryResult<T> {
    data: T;
    error: Error | undefined;
    loading: boolean;
    refetch: () => Promise<void>;
    clearCache: () => void;
    __initialized: boolean;
}

export interface QueryOptions {
    api?: QueryOptionsApi;
    useCache?: boolean;
    skip?: boolean;
}

// hacky solution for legacy refetching of data since you can't access hooks outside of a react render function
const legacyLoadingStatus: Record<string, boolean> = {};
const setLegacyLoadingStatus = (url: string, status: boolean) => {
    legacyLoadingStatus[url] = status;
};

function getApiFromOptions(api: QueryOptionsApi) {
    switch (api) {
        case QueryOptionsApi.Internal:
            return internalApiClient;
        case QueryOptionsApi.Rest:
            return restApiClient;
        case QueryOptionsApi.Addons:
            return getAddonsApiClient();
        case QueryOptionsApi.Market:
            return getMarketApiClient();
        case QueryOptionsApi.Workflows:
            return getWorkflowsApiClient();
        case QueryOptionsApi.Hq:
            return getHqApiClient();
    }

    throw new Error(`Invalid api ${QueryOptionsApi} specified`);
}

// FIXME: no any!
export function useQuery<T = any>(
    url: string,
    initialData: T | any = undefined, // FIXME: no any!
    options: QueryOptions = {}
): QueryResult<T> {
    if (!url) {
        throw new Error('Cannot use useQuery without a url');
    }

    const api = options.api || QueryOptionsApi.Internal;
    const apiClient = getApiFromOptions(api);
    const [state, setState] = useQueryState<T>(url, initialData, options);

    const fetch = async (path: string, ignoreLegacy = true) => {
        if (!state.loading) {
            setState(path, { loading: true });
        }

        if (!ignoreLegacy) {
            setLegacyLoadingStatus(path, true);
        }

        try {
            const response = await apiClient.get(path);
            setState(path, {
                data: response.data,
                loading: false,
                error: undefined,
                __initialized: true,
            });
        } catch (error) {
            setState(path, {
                data: undefined,
                loading: false,
                error,
                __initialized: true,
            });

            if (error?.response && error.response.status >= 500) {
                log.error(error as Error);
            }
        } finally {
            if (!ignoreLegacy) {
                setLegacyLoadingStatus(path, false);
            }
        }
    };

    const refetch = React.useCallback(() => fetch(url), [url]);

    const clearCache = React.useCallback(
        () =>
            setState(url, {
                data: undefined,
                loading: false,
                error: undefined,
                __initialized: false,
            }),
        [url]
    );

    React.useEffect(() => {
        // perform the fetch if there's nothing cached in state and a pending request doesn't already exist
        if (url && (!state.__initialized || !options.useCache) && !options.skip) {
            fetch(url);
        }

        if (window.top) {
            if (!window.top.refetch && api === QueryOptionsApi.Internal) {
                window.top.refetch = path => {
                    if (legacyLoadingStatus[path]) {
                        return;
                    }

                    fetch(path, false);
                }; // hacky solution for legacy refetching of data from internal-api
            }

            if (!window.top.refetchRest && api === QueryOptionsApi.Rest) {
                window.top.refetchRest = path => {
                    if (legacyLoadingStatus[path]) {
                        return;
                    }

                    fetch(path, false);
                }; // hacky solution for legacy refetching of data from rest api
            }
        }
    }, [url, options.skip]); // only automatically requery if the url changes

    return React.useMemo(
        () => ({
            data: state.data || initialData,
            loading: state.loading,
            error: state.error,
            refetch,
            clearCache,
            __initialized: state.__initialized,
        }),
        [clearCache, initialData, refetch, state]
    );
}

export default useQuery;
